import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

// Components
import Button from '../../Button';
import Card3ds from '../../Card3ds';

// State
import googlePayApi from '../../../state/modules/payTypes/googlePayCloudApi';
import { payOrder, setCheckoutIsPayed } from '../../../state/modules/order/actions';
import { isOrderSumLowSelector, minOrderSumSelector } from '../../../state/modules/order/selectors';
import useCheckoutPricing from '../useCheckoutPricing';

// Utils
import { post } from '../../../utils/api';
import { setError } from '../../../state/modules/errors/actions';
import { usePayUTokenize } from '../PayUTokenizeContext';
import { setPayuToken } from '../../../state/modules/payuCard/actions';
export default function PayAction(props) {
  const { className, isDisabled } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  // const stopList = useSelector(state => state.restaurant.stopList);
  const errorMessage = intl.formatMessage({ id: 'privacy.checkbox.error.message' });
  const { payType, deliveryTimeType, currentRestaurant, deliveryTime } = useSelector(
    state => state.order.checkout,
  );
  const { country_code, currency } = useSelector(state => state.city.userCity);
  const isOrderSumLow = useSelector(isOrderSumLowSelector);

  const minOrderSum = useSelector(minOrderSumSelector);
  const { priceIncludingBonuses } = useCheckoutPricing();

  const card3ds = useRef({});
  const [isCard3dsLoaded, setIsCard3dsLoaded] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  const currentCpKey = useSelector(
    state => state.order.checkout.currentRestaurant?.pay_params?.params?.cloudpayments_mid,
  );
  const defaultCpKey = useSelector(
    state => state.restaurant.list[0]?.pay_params?.params?.cloudpayments_mid,
  );

  const cpKey = useMemo(() => currentCpKey || defaultCpKey, [currentCpKey, defaultCpKey]);

  const disabled =
    isDisabled ||
    !currentRestaurant ||
    isOrderSumLow ||
    // stopList.length > 0 ||
    (deliveryTimeType === 'intime' && !deliveryTime.hoursMinutes) ||
    !payType;

  // показываем / скрываем форму оплаты Card3ds
  const callbackAfter3ds = () => {
    dispatch(setCheckoutIsPayed());
  };

  const hideModalCallback = () => {
    setIsCard3dsLoaded(false);
  };

  const showModalCallback = auth3dsData => {
    card3ds.current = auth3dsData;
    setIsCard3dsLoaded(true);
  };

  const payWithApplePay = () => {
    const session = new ApplePaySession(3, {
      countryCode: country_code,
      currencyCode: currency?.code,
      supportedNetworks: ['masterCard', 'visa'],
      merchantCapabilities: ['supports3DS'],
      total: { label: "Papa John's", amount: priceIncludingBonuses },
    });

    session.onvalidatemerchant = event => {
      post({ url: 'apple-pay/start-session', data: { validation_url: event.validationURL } })
        .then(result => {
          if (result.Success) {
            session.completeMerchantValidation(result.Model);
          }
        })
        .catch(() => {});
    };

    session.onpaymentauthorized = event => {
      const onSuccess = result => {
        if (result.order_id) {
          session.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
        } else {
          session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
          session.abort();
        }
      };

      const payOrderParams = {
        pay_type: 'online',
        cloudpayments_data: { apple_pay_cryptogram: JSON.stringify(event.payment.token) },
      };

      return dispatch(payOrder(payOrderParams, onSuccess));
    };

    session.oncancel = () => {};

    session.begin();
  };

  const onSuccess = result => {
    if (result?.payment?.acs_url) {
      showModalCallback({
        payment: result.payment,
        cpKey,
        orderId: result.order_id,
        callbackAfter3ds,
      });
    }
  };

  const payWithGooglePay = () => {
    googlePayApi
      .payment({
        currencyCode: currency?.code,
        totalPrice: priceIncludingBonuses,
        gatewayMerchantId: currentRestaurant?.pay_params?.params?.cloudpayments_mid,
      })
      .then(paymentData => {
        const payOrderParams = {
          pay_type: 'online',
          cloudpayments_data: {
            google_pay_cryptogram: JSON.stringify(paymentData.paymentMethodToken.token),
          },
        };

        return dispatch(payOrder(payOrderParams, onSuccess));
      })
      .catch(() => {});
  };

  const payuCardSelect = useSelector(state => state.payuCard?.cardSelect);
  const isPayU = payType === 'online' && currentRestaurant?.pay_params?.online_pay_system === 5;
  const isAuth = useSelector(state => state.user.isAuthenticated);
  const isCardSaved = useSelector(state => state.order.checkout.isCardSaved);
  const tokenizeCtx = usePayUTokenize();

  const payAction = async () => {
    if (isDisabled) {
      return setError(errorMessage);
    }
    if (payType === 'googlePay') {
      return payWithGooglePay();
    }

    if (payType === 'applePay') {
      return payWithApplePay();
    }

    const payuCardId =
      payuCardSelect?.cardId && payuCardSelect.cardId !== '__NEW__' ? payuCardSelect.cardId : null;
    let payuToken = payuCardSelect?.payuToken;

    if (isPayU && isAuth) {
      if (!payuCardId && !payuToken && tokenizeCtx?.tokenizeRef?.current) {
        setIsPaying(true);
        try {
          const tokenizeFn = tokenizeCtx.tokenizeRef.current;
          payuToken = await tokenizeFn();
          if (payuToken) {
            dispatch(setPayuToken(payuToken));
          }
        } catch (e) {
          setIsPaying(false);
          setError(e?.message || intl.formatMessage({ id: 'order.payment.error' }));
          return;
        }
        setIsPaying(false);
      }
      if (!payuCardId && !payuToken) {
        setError(intl.formatMessage({ id: 'order.payment.selectOrEnterCard' }));
        return;
      }
    }

    setIsPaying(true);
    const payOrderParams = {};
    if (isPayU && isAuth) {
      if (payuCardId) {
        payOrderParams.payu_card_id = payuCardId;
      } else {
        payOrderParams.payu_token = payuToken;
      }
      payOrderParams.save_card = isCardSaved;
    }
    await dispatch(payOrder(payOrderParams, onSuccess));
    setIsPaying(false);

    return true;
  };

  return (
    <>
      <Button className={className} disabled={disabled} isLoading={isPaying} onClick={payAction}>
        {intl.formatMessage(
          {
            id:
              // eslint-disable-next-line no-nested-ternary
              isOrderSumLow
                ? 'order.minSum'
                : payType === 'online' || payType === 'applePay' || payType === 'googlePay'
                ? 'button.pay'
                : 'button.order',
          },
          { sum: minOrderSum, currency: currency?.unit },
        )}
      </Button>
      <Card3ds
        auth3dsData={card3ds.current.payment}
        orderId={card3ds.current.orderId}
        callbackAfter3ds={card3ds.current.callbackAfter3ds}
        onClose={hideModalCallback}
        isOpen={isCard3dsLoaded}
      />
    </>
  );
}

PayAction.defaultProps = {
  className: '',
  isDisabled: false,
};

PayAction.propTypes = {
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
};
